import React, { useState, useEffect } from 'react';
import { PageView } from './types';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LeadModal } from './components/LeadModal';
import { HomePage } from './pages/HomePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { WhyMusePage } from './pages/WhyMusePage';
import { BlogPage } from './pages/BlogPage';
import { OwnerLandingPage } from './pages/OwnerLandingPage';
import { OpsLandingPage } from './pages/OpsLandingPage';
import { TechLeadLandingPage } from './pages/TechLeadLandingPage';

const pageToPathMap: Record<PageView, string> = {
  home: '/',
  features: '/features',
  'why-muse': '/why-muse',
  blog: '/blog',
  consolidation: '/consolidation',
  ops: '/ops',
  forge: '/forge',
};

const getPageFromPath = (): PageView => {
  const rawPath = window.location.pathname.toLowerCase().trim().replace(/\/+$/, '');
  const path = rawPath === '' ? '/' : rawPath;

  if (path === '/consolidation') return 'consolidation';
  if (path === '/ops' || path === '/outage-response') return 'ops';
  if (path === '/forge' || path === '/sandbox') return 'forge';
  if (path === '/features' || path === '/capabilities') return 'features';
  if (path === '/why-muse' || path === '/philosophy') return 'why-muse';
  if (path === '/blog' || path === '/articles') return 'blog';
  return 'home';
};

export const App: React.FC = () => {
  const [activePage, setActivePageState] = useState<PageView>(getPageFromPath);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState<boolean>(false);

  const setActivePage = (page: PageView) => {
    setActivePageState(page);
    const targetPath = pageToPathMap[page] || '/';
    if (window.location.pathname.toLowerCase() !== targetPath.toLowerCase()) {
      window.history.pushState({}, '', targetPath);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setActivePageState(getPageFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openLeadModal = () => setIsLeadModalOpen(true);
  const closeLeadModal = () => setIsLeadModalOpen(false);

  return (
    <div className="app-container">
      {/* Background Mesh Gradient */}
      <div className="bg-mesh-gradient" />

      {/* Navigation Header */}
      <Navbar
        activePage={activePage}
        setActivePage={setActivePage}
        onOpenLeadModal={openLeadModal}
      />

      {/* Main Page View Content */}
      <main className="main-content">
        {activePage === 'home' && (
          <HomePage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'features' && (
          <FeaturesPage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'why-muse' && (
          <WhyMusePage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'blog' && (
          <BlogPage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'consolidation' && (
          <OwnerLandingPage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'ops' && (
          <OpsLandingPage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
        {activePage === 'forge' && (
          <TechLeadLandingPage setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
        )}
      </main>

      {/* Lead Capture Modal */}
      <LeadModal isOpen={isLeadModalOpen} onClose={closeLeadModal} />

      {/* Footer */}
      <Footer setActivePage={setActivePage} onOpenLeadModal={openLeadModal} />
    </div>
  );
};
export default App;
